import { effect, inject, Injectable } from '@angular/core';
import { ItemsFormWithCustomVariations, ItemVariationsFormGroups, VARIATIONS_FE_KEYS } from './item-variations.model';
import { ItemDefault, ItemFields } from 'src/app/features/items/models/items-form.model';
import { CustomFormControl, CustomFormGroup } from '@tilby/tilby-ui-lib/components/tilby-magic-form';
import { VariationsFormService } from './variations/variations-form.service';
import { CombinationsFormService } from './combinations/combinations-form.service';
import { Items } from 'tilby-models';

@Injectable()
export class ItemVariationsSectionService {

  readonly variationsFormService = inject(VariationsFormService);
  readonly combinationsFormService = inject(CombinationsFormService);

  private itemVariationsForm!: ItemVariationsFormGroups;

  get form() {
    return this.itemVariationsForm;
  }

  constructor() {
    effect(() => {
        const variationNameChanged = this.variationsFormService.variationNameChanged()?.index;
        if(variationNameChanged >= 0) {
          this.combinationsFormService.updateVariationNameInCombination(this.variationsFormService.variationsArray || []);
          this.combinationsFormService.updateNameComposition();
        }
    })

    effect(() => {
      const variationValueNameChanged = this.variationsFormService.variationValueNameChanged()?.index;
      if(variationValueNameChanged >= 0) {
        const variationValues = this.variationsFormService.variationsArray?.value.at(variationValueNameChanged)?.variation_values || [];
        this.combinationsFormService.updateVariationValueValidators(variationValueNameChanged, variationValues);
        this.combinationsFormService.triggerVariationValueNameListener();
        setTimeout(() => this.combinationsFormService.updateNameComposition());
      }
    }, { allowSignalWrites: true })

    effect(() => {
        const variationsOrderChanged = this.variationsFormService.variationsOrderChanged();
        if(variationsOrderChanged) {
          this.combinationsFormService.syncCombinationsWithNewVariationOrder(variationsOrderChanged);
          this.combinationsFormService.updateVariationNameInCombination(this.variationsFormService.variationsArray || []);
          this.combinationsFormService.updateNameComposition();
          this.combinationsFormService.triggerVariationValueNameListener();
        }
    }, { allowSignalWrites: true })

    effect(() => {
      const variantAdded = this.variationsFormService.variationAdded();
      if(variantAdded?.length) {
        for(let variation of variantAdded) {
          this.combinationsFormService.updateAddedVariationInCombination(variation);
          this.combinationsFormService.triggerVariationValueNameListener();
        }
      }
    }, { allowSignalWrites: true })

    effect(() => {
        const variationValuesOrderChanged = this.variationsFormService.variationValuesOrderChanged();
        if(variationValuesOrderChanged) {
          this.combinationsFormService.triggerVariationValueNameListener();
        }
    }, { allowSignalWrites: true })
  }
  
  createForm(item: ItemFields = new ItemDefault(),hiddenFields?:Array<keyof ItemFields>) {
    this.itemVariationsForm = new CustomFormGroup<ItemsFormWithCustomVariations>({
      is_group_item: new CustomFormControl(
        item.is_group_item || false
      ),
      split_group_components: new CustomFormControl(
        item.split_group_components || false
      ),
      combinationsActive: new CustomFormControl(
        item.combinations && item.combinations.length ? true: false
      )
    })

    return this.itemVariationsForm;
  }

  resetFormValues(item: Items = new ItemDefault()) {
    this.itemVariationsForm.patchValue({
      is_group_item: item.is_group_item || false,
      split_group_components: item.split_group_components || false,
      combinationsActive: item.combinations && item.combinations.length ? true: false
    });
  }
  
  getVariationsSectionFields(currentItem: Items) {
    // Clono l'oggetto di destinazione per non mutare l'originale
    const updatedItem = { ...currentItem }; 
    
    // Recupero tutte le chiavi (nomi dei campi) presenti nel form
    const itemVariationsFieldsToMap = Object.keys(this.itemVariationsForm.controls);

    // Ciclo su tutte le chiavi trovate nel form
    itemVariationsFieldsToMap.forEach(key => {

      if (!VARIATIONS_FE_KEYS.includes(key)) {
        const control = this.itemVariationsForm.get(key);

        if(control) {
            let value = control.value;
            // Assegno il valore all'oggetto.
            (updatedItem as any)[key] = value
        }
      } 
    })

    const variations = this.variationsFormService.getVariationsFields();
    (updatedItem as any)['variations'] = variations;

    const combinations = this.combinationsFormService.getCombinationsFields();
    (updatedItem as any)['combinations'] = combinations;

    return updatedItem;
  }

  get is_group_item() {
    return this.itemVariationsForm?.controls.is_group_item;
  }

  get split_group_components() {
    return this.itemVariationsForm?.controls.split_group_components;
  }

  get combinationsActive() {
    return this.itemVariationsForm?.controls.combinationsActive;
  }
}
